import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    Button,
    IconButton,
    Paper,
    Autocomplete,
    TextField,
    Avatar
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';

import { useContactUpdate } from '../../../hooks/useContactUpdate';
import { contactDetailStyles, animations } from './styles';

const ContactConnections = ({
    contact,
    allContacts = [],
    onContactUpdate,
    onRelatedContactClick,
    onSnackbar,
    t = {}
}) => {
    const { updateContactPartial } = useContactUpdate();
    // Connection management state
    const [isAddingRelation, setIsAddingRelation] = useState(false);
    const [selectedRelatedContact, setSelectedRelatedContact] = useState(null);
    const [selectedRelationType, setSelectedRelationType] = useState('');
    
    const [relationTypes, setRelationTypes] = useState(() => {
        const savedTypes = localStorage.getItem('userRelationTypes');
        const defaultTypes = ['Classmate', 'Colleague', 'Relative', 'Friend', 'Partner', 'Client'];
        const savedTypesArray = savedTypes ? JSON.parse(savedTypes) : [];
        return [...new Set([...defaultTypes, ...savedTypesArray])];
    });

    // Мемоизируем фильтрацию контактов
    const otherContacts = useMemo(() => {
        if (!contact?._id || !allContacts.length) return [];
        return allContacts.filter(c => c._id !== contact._id);
    }, [allContacts, contact?._id]);

    // Handlers
    const handleRelatedContactChange = (event, newValue) => {
        setSelectedRelatedContact(newValue);
    };
    
    const handleRelationTypeChange = (event, newValue) => {
        if (typeof newValue === 'string') {
            setSelectedRelationType(newValue);
        } else if (newValue && newValue.inputValue) {
            const newType = newValue.inputValue;
            
            if (!relationTypes.includes(newType)) {
                const updatedTypes = [...relationTypes, newType];
                setRelationTypes(updatedTypes);
                localStorage.setItem('userRelationTypes', JSON.stringify(updatedTypes));
            }
            setSelectedRelationType(newType);
        } else {
            setSelectedRelationType(newValue);
        }
    };
    
    const handleAddRelation = async () => {
        if (!selectedRelatedContact) return;
        
        let relationType;
        if (typeof selectedRelationType === 'string') {
            relationType = selectedRelationType;
        } else if (selectedRelationType && selectedRelationType.inputValue) {
            relationType = selectedRelationType.inputValue;
        } else if (selectedRelationType) {
            relationType = selectedRelationType.title || selectedRelationType;
        } else {
            return;
        }
        
        if (!selectedRelatedContact._id) {
            console.error('Selected contact has no _id:', selectedRelatedContact);
            onSnackbar({
                open: true,
                message: t.invalidContact || 'Invalid contact selected',
                severity: 'error'
            });
            return;
        }
        
        const newRelation = {
            contactId: selectedRelatedContact._id,
            contactName: selectedRelatedContact.name || '',
            type: relationType
        };
        
        const relationExists = (contact.relations || []).some(
            r => r.contactId === newRelation.contactId && r.type === newRelation.type
        );
        
        if (!relationExists) {
            const updatedRelations = [...(contact.relations || []), newRelation];
            
            try {
                const updatedContact = await updateContactPartial(contact._id, {
                    relations: updatedRelations
                }, contact);
                
                setSelectedRelatedContact(null);
                setSelectedRelationType('');
                setIsAddingRelation(false);
                
                if (onContactUpdate) {
                    onContactUpdate(updatedContact);
                }
                
                onSnackbar({
                    open: true,
                    message: t.relationAdded || 'Contact connection added successfully',
                    severity: 'success'
                });
            } catch (error) {
                console.error('Error adding relation:', error);
                onSnackbar({
                    open: true,
                    message: error.message || 'Error adding connection',
                    severity: 'error'
                });
            }
        }
    };
    
    const handleRemoveRelation = async (relationToRemove) => {
        try {
            const updatedRelations = (contact.relations || []).filter(
                r => !(r.contactId === relationToRemove.contactId && r.type === relationToRemove.type)
            );
            
            const updatedContact = await updateContactPartial(contact._id, {
                relations: updatedRelations
            }, contact);
            
            if (onContactUpdate) {
                onContactUpdate(updatedContact);
            }
            
            onSnackbar({
                open: true,
                message: t.relationRemoved || 'Contact connection removed successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error removing relation:', error);
            onSnackbar({
                open: true,
                message: error.message || 'Error removing connection',
                severity: 'error'
            });
        }
    };

    return (
        <Box sx={{ ...contactDetailStyles.sectionContainer, ...animations.slideInRight }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ ...contactDetailStyles.sectionTitle, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon fontSize="small" />
                    {t.connections || 'Connections'}
                </Typography>
                <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    size="small"
                    onClick={() => setIsAddingRelation(!isAddingRelation)}
                >
                    {isAddingRelation ? (t.cancel || 'Cancel') : (t.addRelation || 'Add Contact')}
                </Button>
            </Box>
            
            {/* Add relation form */}
            {isAddingRelation && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Autocomplete
                        options={otherContacts}
                        getOptionLabel={(option) => option.name || ''}
                        value={selectedRelatedContact}
                        onChange={handleRelatedContactChange}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label={t.selectContact || 'Select Contact'} 
                                fullWidth 
                                margin="normal"
                                size="small"
                            />
                        )}
                    />
                    
                    <Autocomplete
                        freeSolo
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        value={selectedRelationType}
                        onChange={handleRelationTypeChange}
                        filterOptions={(options, params) => {
                            const filtered = options.filter(option => 
                                option.toLowerCase().includes(params.inputValue.toLowerCase())
                            );
                            
                            const isExisting = options.some(option => 
                                params.inputValue === option || 
                                option.toLowerCase() === params.inputValue.toLowerCase()
                            );
                            
                            if (params.inputValue !== '' && !isExisting) {
                                filtered.push({
                                    inputValue: params.inputValue,
                                    title: `Add "${params.inputValue}"`
                                });
                            }
                            
                            return filtered;
                        }}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                                return option;
                            }
                            if (option.inputValue) {
                                return option.inputValue;
                            }
                            return option.title;
                        }}
                        renderOption={(props, option) => (
                            <li {...props}>
                                {typeof option === 'string' ? option : option.title}
                            </li>
                        )}
                        options={relationTypes}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t.relationType || 'Connection Type'}
                                placeholder={t.newRelationType || "Type or select connection type"}
                                size="small"
                                fullWidth
                                margin="normal"
                                helperText={t.relationTypeHelper || "Type to search or create new type"}
                            />
                        )}
                    />
                    
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddRelation}
                            disabled={!selectedRelatedContact || 
                                     (!selectedRelationType || 
                                      (typeof selectedRelationType === 'object' && 
                                       !selectedRelationType.inputValue && 
                                       !selectedRelationType.title))}
                            startIcon={<AddIcon />}
                        >
                            {t.add || "Add"}
                        </Button>
                    </Box>
                </Paper>
            )}
            
            {/* Display existing relations */}
            {contact.relations && contact.relations.length > 0 ? (
                <List dense disablePadding>
                    {contact.relations.map((relation, index) => (
                        <ListItem 
                            key={`${relation.contactId || index}-${index}`} 
                            disablePadding
                            sx={{ 
                                mb: 1, 
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'primary.main',
                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                                '&:hover': { 
                                    bgcolor: 'rgba(25, 118, 210, 0.15)', 
                                    cursor: 'pointer',
                                    transform: 'translateX(4px)',
                                    transition: 'all 0.2s ease'
                                } 
                            }}
                        >
                            <ListItemIcon onClick={() => onRelatedContactClick(relation.contactId)}>
                                <Avatar 
                                    sx={{ 
                                        width: 36, 
                                        height: 36, 
                                        bgcolor: 'primary.main',
                                        border: '2px solid',
                                        borderColor: 'primary.dark'
                                    }}
                                >
                                    <PeopleIcon fontSize="small" />
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText 
                                primary={
                                    <span style={{ 
                                        fontWeight: 'bold',
                                        fontSize: '0.875rem',
                                        color: 'text.primary'
                                    }}>
                                        {relation.contactName}
                                    </span>
                                }
                                secondary={
                                    <span style={{ 
                                        color: 'primary.main',
                                        fontWeight: 'medium',
                                        fontStyle: 'italic',
                                        fontSize: '0.875rem'
                                    }}>
                                        {relation.type}
                                    </span>
                                }
                                onClick={() => onRelatedContactClick(relation.contactId)}
                            />
                            <ListItemSecondaryAction>
                                <IconButton 
                                    edge="end" 
                                    size="small" 
                                    onClick={() => handleRemoveRelation(relation)}
                                    sx={{
                                        color: 'error.main',
                                        '&:hover': {
                                            bgcolor: 'error.light',
                                            color: 'error.contrastText'
                                        }
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {t.noConnections || 'No connections yet'}
                </Typography>
            )}

            {/* Common Contacts Section */}
            {contact.commonContacts && Array.isArray(contact.commonContacts) && contact.commonContacts.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {t.commonContacts || 'Common Contacts'}
                    </Typography>
                    <List dense disablePadding>
                        {contact.commonContacts.map((commonContactId, index) => {
                            const commonContact = allContacts.find(c => c._id === commonContactId);
                            if (!commonContact) return null;
                            
                            return (
                                <ListItem 
                                    key={`common-${commonContactId || index}`} 
                                    disablePadding
                                    sx={{ 
                                        mb: 1, 
                                        p: 1,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'secondary.main',
                                        bgcolor: 'rgba(156, 39, 176, 0.08)',
                                        '&:hover': { 
                                            bgcolor: 'rgba(156, 39, 176, 0.15)', 
                                            cursor: 'pointer',
                                            transform: 'translateX(4px)',
                                            transition: 'all 0.2s ease'
                                        } 
                                    }}
                                    onClick={() => onRelatedContactClick(commonContactId)}
                                >
                                    <ListItemIcon>
                                        <Avatar 
                                            sx={{ 
                                                width: 36, 
                                                height: 36, 
                                                bgcolor: 'secondary.main',
                                                border: '2px solid',
                                                borderColor: 'secondary.dark'
                                            }}
                                        >
                                            <PeopleIcon fontSize="small" />
                                        </Avatar>
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={
                                            <span style={{ 
                                                fontWeight: 'bold',
                                                fontSize: '0.875rem',
                                                color: 'text.primary'
                                            }}>
                                                {commonContact.name || ''}
                                            </span>
                                        }
                                        secondary={
                                            <span style={{ 
                                                color: 'secondary.main',
                                                fontWeight: 'medium',
                                                fontStyle: 'italic',
                                                fontSize: '0.875rem'
                                            }}>
                                                {t.commonContact || 'Common Contact'}
                                            </span>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            )}
        </Box>
    );
};

export default ContactConnections; 
